import type { LocalizedDict } from '../index'
import type { StudioBakeoffItemRead } from '../../api/generated/model'

// Single-product Magnific bake-off panel (features/admin/MagnificTestPanel.tsx).
// Operation names (Image Relight, Seedream, Mystic) are provider product names
// and stay in the component untranslated.

export type MagnificTestPanelStrings = {
  statuses: Record<StudioBakeoffItemRead['status'], string>
  unsafeOutput: string
  failureCode: (code: string) => string
  failedBeforeReview: string
  submittedToast: (name: string) => string
  submittedDescription: string
  submitProblem: string
  notConfirmedToast: string
  notConfirmedDescription: string
  title: string
  subtitle: string
  simulationWarning: string
  credentialsWarning: string
  batchId: string
  loadBatch: string
  loadBatchError: string
  batchStatus: (status: string) => string
  refreshing: string
  comparisonError: string
  runTitle: (name: string | null) => string
  runDescription: string
  estimatedCost: (credits: number) => string
  enqueueNote: string
  cancel: string
  confirmPaid: string
  recipe: (id: string) => string
  reference: string
  generated: string
  referenceAlt: (name: string) => string
  generatedAlt: (name: string) => string
  method: string
  credits: string
  estimatedCredits: (credits: number) => string
  reportedSeparately: string
  status: string
  runAria: (name: string) => string
  runInMagnific: string
  openFullSize: string
  notInDrive: string
}

export const magnificTestPanelI18n: LocalizedDict<MagnificTestPanelStrings> = {
  en: {
    statuses: {
      planned: 'Planned',
      blocked: 'Blocked',
      submitted: 'Submitted',
      generating: 'Generating',
      pending_review: 'Ready for review',
      failed: 'Failed',
      cancelled: 'Cancelled',
    },
    unsafeOutput:
      'No safe result was ingested into Drive; transient provider output is intentionally not exposed.',
    failureCode: (code) => `Failure: ${code}`,
    failedBeforeReview: 'Generation failed before review.',
    submittedToast: (name) => `${name} submitted`,
    submittedDescription: 'PEXIFY will refresh the durable job state automatically.',
    submitProblem: 'Could not submit this Magnific operation.',
    notConfirmedToast: 'Magnific submission was not confirmed',
    notConfirmedDescription: 'Durable batch state was refreshed. PEXIFY did not retry automatically.',
    title: 'Single-product Magnific test',
    subtitle:
      'Resume an existing three-method bake-off. This does not change the product gallery or primary image.',
    simulationWarning:
      'Test execution is unavailable in simulation mode. Open a real-API build to load a batch.',
    credentialsWarning:
      'Activate saved Magnific credentials before submitting a paid operation. Existing batches can still be reviewed.',
    batchId: 'Batch ID',
    loadBatch: 'Load batch',
    loadBatchError: 'Could not load this batch.',
    batchStatus: (status) => `Batch: ${status}`,
    refreshing: 'Refreshing every 3 seconds',
    comparisonError: 'Could not load image comparison.',
    runTitle: (name) => `Run ${name ?? 'operation'}?`,
    runDescription:
      'This may spend Magnific credits. The exact cost is reported separately in Magnific and this item has only one allowed attempt.',
    estimatedCost: (credits) => `Estimated Magnific cost: ${credits} credits.`,
    enqueueNote:
      'PEXIFY will enqueue one durable request. It will not retry automatically if the outcome is ambiguous.',
    cancel: 'Cancel',
    confirmPaid: 'Confirm paid generation',
    recipe: (id) => `Recipe ${id}`,
    reference: 'Reference',
    generated: 'Generated',
    referenceAlt: (name) => `Reference for ${name}`,
    generatedAlt: (name) => `Generated result for ${name}`,
    method: 'Method:',
    credits: 'Credits:',
    estimatedCredits: (credits) => `Estimated ${credits} Magnific credits`,
    reportedSeparately: 'reported separately in Magnific',
    status: 'Status:',
    runAria: (name) => `Run ${name} in Magnific`,
    runInMagnific: 'Run in Magnific',
    openFullSize: 'Open full size',
    notInDrive: 'Not available in Drive',
  },
  tr: {
    statuses: {
      planned: 'Planlandı',
      blocked: 'Engellendi',
      submitted: 'Gönderildi',
      generating: 'Üretiliyor',
      pending_review: 'İncelemeye hazır',
      failed: 'Başarısız',
      cancelled: 'İptal edildi',
    },
    unsafeOutput:
      'Drive’a güvenli bir sonuç alınmadı; sağlayıcının geçici çıktısı bilerek gösterilmiyor.',
    failureCode: (code) => `Hata: ${code}`,
    failedBeforeReview: 'Üretim incelemeden önce başarısız oldu.',
    submittedToast: (name) => `${name} gönderildi`,
    submittedDescription: 'PEXIFY iş durumunu otomatik olarak yenileyecek.',
    submitProblem: 'Bu Magnific işlemi gönderilemedi.',
    notConfirmedToast: 'Magnific gönderimi doğrulanmadı',
    notConfirmedDescription: 'Paket durumu yenilendi. PEXIFY otomatik olarak yeniden denemedi.',
    title: 'Tek ürünlü Magnific testi',
    subtitle:
      'Mevcut üç yöntemli karşılaştırmaya devam et. Bu işlem ürün galerisini veya ana görseli değiştirmez.',
    simulationWarning:
      'Simülasyon modunda test çalıştırılamaz. Bir paketi yüklemek için gerçek API sürümünü aç.',
    credentialsWarning:
      'Ücretli bir işlem göndermeden önce kayıtlı Magnific bilgilerini etkinleştir. Mevcut paketler yine de incelenebilir.',
    batchId: 'Paket ID',
    loadBatch: 'Paketi yükle',
    loadBatchError: 'Bu paket yüklenemedi.',
    batchStatus: (status) => `Paket: ${status}`,
    refreshing: 'Her 3 saniyede yenileniyor',
    comparisonError: 'Görsel karşılaştırması yüklenemedi.',
    runTitle: (name) => `${name ?? 'İşlem'} çalıştırılsın mı?`,
    runDescription:
      'Bu işlem Magnific kredisi harcayabilir. Kesin maliyet Magnific’te ayrıca raporlanır ve bu öğe için yalnızca bir deneme hakkı var.',
    estimatedCost: (credits) => `Tahmini Magnific maliyeti: ${credits} kredi.`,
    enqueueNote:
      'PEXIFY tek bir kalıcı istek kuyruğa alır. Sonuç belirsiz kalırsa otomatik olarak yeniden denemez.',
    cancel: 'Vazgeç',
    confirmPaid: 'Ücretli üretimi onayla',
    recipe: (id) => `Tarif ${id}`,
    reference: 'Referans',
    generated: 'Üretilen',
    referenceAlt: (name) => `${name} için referans`,
    generatedAlt: (name) => `${name} için üretilen sonuç`,
    method: 'Yöntem:',
    credits: 'Kredi:',
    estimatedCredits: (credits) => `Tahmini ${credits} Magnific kredisi`,
    reportedSeparately: 'Magnific’te ayrıca raporlanır',
    status: 'Durum:',
    runAria: (name) => `${name} işlemini Magnific’te çalıştır`,
    runInMagnific: 'Magnific’te çalıştır',
    openFullSize: 'Tam boyutta aç',
    notInDrive: 'Drive’da yok',
  },
}
